import {all, select, takeLatest} from '@redux-saga/core/effects';
import {BeneficiaryPersonDto} from 'Beneficiaries/api/api';
import beneficiariesApi from 'Beneficiaries/api/beneficiariesApi';
import {BeneficiariesActionTypes, changeBeneficiaries, fetchBeneficiaries} from 'Beneficiaries/ducks/beneficiaries';
import {
  BeneficiaryDetailActionTypes,
  changeBeneficiaryDetails,
  deleteBeneficiaryPerson,
  validateAndSaveBeneficiaryPolicy,
} from 'Beneficiaries/ducks/beneficiaryDetail';
import {
  BeneficiaryFormActionTypes,
  changeBeneficiaryPerson,
  changePolicyBeneficiaryPersons,
  validateAndSaveBeneficiaryPercents,
  validateAndSubmitForm,
  validateBeneficiaryPercent,
} from 'Beneficiaries/ducks/beneficiaryForm';
import {
  BeneficiaryPerson,
  ValidBeneficiaryPerson,
  ValidBeneficiaryPersonDateOfBirth,
  ValidPersonType,
} from 'Beneficiaries/models/BeneficiaryPerson';
import {BeneficiaryPersonPercentage} from 'Beneficiaries/models/BeneficiaryPersonPercentage';
import {BeneficiaryPolicy} from 'Beneficiaries/models/BeneficiaryPolicy';
import {BeneficiariesRouteConstants} from 'Beneficiaries/Routes';
import {push} from 'connected-react-router';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult, Loaded, Loading, LoadingAdditional} from 'Core/models/Async';
import {ValidEmail} from 'Core/models/Email';
import {NonEmptyString} from 'Core/models/EmptyString';
import {Page} from 'Core/models/Page';
import {ValidPhoneNumber} from 'Core/models/PhoneNumber';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {DateTime} from 'luxon';
import {call, put, takeEvery} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {fetchBeneficiaryDetails, updateBeneficiaryDetails} from './../ducks/beneficiaryDetail';

const mapBeneficiaryPersonToDto = (beneficiaryPerson: BeneficiaryPerson): BeneficiaryPersonDto => ({
  id: beneficiaryPerson.id,
  identifier: beneficiaryPerson.identifier?.value,
  firstName: beneficiaryPerson.firstName,
  middleName: beneficiaryPerson.middleName,
  lastName: beneficiaryPerson.lastName,
  dateOfBirth: !!beneficiaryPerson.dateOfBirth?.value
    ? DateTime.fromJSDate(beneficiaryPerson.dateOfBirth?.value).toISODate()
    : undefined,
  gender: beneficiaryPerson.gender,
  relationship: beneficiaryPerson.relationship,
  phoneNumber: beneficiaryPerson.phoneNumber?.phoneNumber,
  email: beneficiaryPerson.email?.address,
  beneficiaryPercent: beneficiaryPerson.beneficiaryPercent || 0,
  personType: beneficiaryPerson.personType?.id!,
  address1: beneficiaryPerson.address1,
  address2: beneficiaryPerson.address2,
  address3: beneficiaryPerson.address3,
  city: beneficiaryPerson.city,
  state: beneficiaryPerson.state,
  zipcode: beneficiaryPerson.zipcode,
  signatureDate: !!beneficiaryPerson.signatureDate
    ? DateTime.fromJSDate(beneficiaryPerson.signatureDate).toISODate()
    : undefined,
  sourceSystemIdentifier: beneficiaryPerson.sourceSystemIdentifier?.value,
});

const mapDtoToBeneficiaryPerson = (beneficiaryPersonDto: BeneficiaryPersonDto): BeneficiaryPerson => ({
  id: beneficiaryPersonDto.id || undefined,
  // On load, identifier is always a string not a ValidTaxIdentifier object
  identifier: NonEmptyString.create(beneficiaryPersonDto.identifier || ''),
  firstName: beneficiaryPersonDto.firstName || undefined,
  middleName: beneficiaryPersonDto.middleName || undefined,
  lastName: beneficiaryPersonDto.lastName || undefined,
  dateOfBirth: ValidBeneficiaryPersonDateOfBirth.create(
    beneficiaryPersonDto.dateOfBirth ? toFormattedDateAsUTC(beneficiaryPersonDto.dateOfBirth) : undefined
  ),
  gender: beneficiaryPersonDto.gender || undefined,
  relationship: beneficiaryPersonDto.relationship || undefined,
  phoneNumber: ValidPhoneNumber.create(beneficiaryPersonDto.phoneNumber || ''),
  email: ValidEmail.create(beneficiaryPersonDto.email || ''),
  beneficiaryPercent: beneficiaryPersonDto.beneficiaryPercent || undefined,
  personType: ValidPersonType.create(beneficiaryPersonDto.personType),
  address1: beneficiaryPersonDto.address1 || undefined,
  address2: beneficiaryPersonDto.address2 || undefined,
  address3: beneficiaryPersonDto.address3 || undefined,
  city: beneficiaryPersonDto.city || undefined,
  state: beneficiaryPersonDto.state || undefined,
  zipcode: beneficiaryPersonDto.zipcode || undefined,
  signatureDate: beneficiaryPersonDto.signatureDate
    ? new Date(toFormattedDateAsUTC(beneficiaryPersonDto.signatureDate))
    : undefined,
  sourceSystemIdentifier: beneficiaryPersonDto.sourceSystemIdentifier
    ? NonEmptyString.create(beneficiaryPersonDto.sourceSystemIdentifier)
    : undefined,
  markedAsDeleted: false,
});

function* fetchBeneficiariesSaga() {
  yield takeLatest(BeneficiariesActionTypes.FETCH_BENEFICIARIES, getBeneficiariesFromApi);
}

function* getBeneficiariesFromApi(action: ReturnType<typeof fetchBeneficiaries>) {
  if (action.payload.pageOption) {
    yield put(changeBeneficiaries(new LoadingAdditional(action.payload.pageOption)));
  } else {
    yield put(changeBeneficiaries(new Loading(action.payload.pageOption)));
  }

  const result: AsyncResult<Page<BeneficiaryPolicy>> = yield action.payload.pageOption
    ? call(mergeWithNextPage, {existingPage: action.payload.pageOption})
    : call(beneficiariesApi.fetchBeneficiaries, action.payload.filter);

  yield pipe(
    result,
    E.map(value => new Loaded(value)),
    E.fold(
      value => put(changeBeneficiaries(value)),
      value => put(changeBeneficiaries(value))
    )
  );
}

function* deleteBeneficiaryPersonSaga() {
  yield takeEvery(BeneficiaryDetailActionTypes.DELETE_BENEIFICIARY_PERSON, deleteBeneficiaryPersonApi);
}

function* deleteBeneficiaryPersonApi(action: ReturnType<typeof deleteBeneficiaryPerson>) {
  const result = yield call(beneficiariesApi.deleteBeneficiaryPerson, action.payload.id, action.payload.policyNumber);

  if (E.isRight(result)) {
    yield put(createSnackbar('Beneficiary Person Successfully Deleted'));
    yield put(fetchBeneficiaryDetails(action.payload.policyNumber));
  } else {
    yield put(createSnackbar('Unable to Delete Beneficiary Person'));
  }
}

function* fetchBeneficiaryDetailsSaga() {
  yield takeLatest(BeneficiaryDetailActionTypes.FETCH_BENEFICIARY_DETAILS, getBeneficiaryDetailsFromApi);
}

function* getBeneficiaryDetailsFromApi(action: ReturnType<typeof fetchBeneficiaryDetails>) {
  const result: AsyncResult<BeneficiaryPersonDto[]> = yield call(
    beneficiariesApi.getBeneficiaryDetails,
    action.payload.policyNumber
  );
  if (E.isRight(result)) {
    yield put(changeBeneficiaryDetails(new Loaded(result.right?.map(mapDtoToBeneficiaryPerson) || [])));
  } else {
    yield put(push(BeneficiariesRouteConstants.BENEFICIARIES));
  }
}

function* updateBeneficiaryPerson(beneficiaryPerson: BeneficiaryPerson, policyNumber: string) {
  const result: AsyncResult<BeneficiaryPersonDto> = yield call(
    beneficiariesApi.updateBeneficiaryPerson,
    mapBeneficiaryPersonToDto(beneficiaryPerson),
    policyNumber
  );

  if (E.isRight(result)) {
    yield put(fetchBeneficiaryDetails(policyNumber));
    yield put(createSnackbar('Beneficiary Person Successfully Updated'));
  } else {
    yield put(createSnackbar('Unable to Update Beneficiary Person'));
  }
}

function* addBeneficiaryPerson(beneficiaryPerson: BeneficiaryPerson, policyNumber: string) {
  const result: AsyncResult<BeneficiaryPersonDto> = yield call(
    beneficiariesApi.addBeneficiaryPerson,
    mapBeneficiaryPersonToDto(beneficiaryPerson),
    policyNumber
  );

  if (E.isRight(result)) {
    yield put(push(BeneficiariesRouteConstants.BENEFICIARY_UPDATE(result.right.id!, policyNumber)));
    yield put(createSnackbar('Beneficiary Person Successfully Added'));
  } else {
    yield put(createSnackbar('Unable to Add Beneficiary Person'));
  }
}

function* validateAndSubmitFormSaga() {
  yield takeEvery(BeneficiaryFormActionTypes.VALIDATE_AND_SUBMIT_BENEFICIARY_FORM, validateAndSubmitFormApi);
}

function* validateAndSubmitFormApi(action: ReturnType<typeof validateAndSubmitForm>) {
  const beneficiaryPerson = ValidBeneficiaryPerson.create(action.payload.beneficiaryPerson);

  if (beneficiaryPerson instanceof ValidBeneficiaryPerson) {
    yield put(changeBeneficiaryPerson(new Loading(action.payload.beneficiaryPerson)));

    if (!action.payload.beneficiaryPerson.id) {
      yield addBeneficiaryPerson(action.payload.beneficiaryPerson, action.payload.policyNumber);
    } else {
      yield updateBeneficiaryPerson(action.payload.beneficiaryPerson, action.payload.policyNumber);
    }
  } else {
    yield put(
      changeBeneficiaryPerson(
        new Loaded({
          ...action.payload.beneficiaryPerson,
          ...beneficiaryPerson,
        })
      )
    );
  }
}

function* validateAndSaveBeneficiaryPercentagesSaga() {
  yield takeEvery(
    BeneficiaryFormActionTypes.VALIDATE_AND_SAVE_BENEFICIARY_PERCENTAGES,
    validateAndSaveBeneficiaryPercentagesApi
  );
}

function* validateAndSaveBeneficiaryPercentagesApi(action: ReturnType<typeof validateAndSaveBeneficiaryPercents>) {
  yield put(validateBeneficiaryPercent());

  const {beneficiaryPercentageError} = yield select((state: ReducerState) => state.beneficiaryForm);

  const beneficiaryPersonPercentages = action.payload.beneficiaryPersons.map(
    bp =>
      ({
        percentage: bp.beneficiaryPercent,
        id: bp.id,
        personType: bp.personType?.id,
      } as BeneficiaryPersonPercentage)
  );

  if (!beneficiaryPercentageError) {
    yield put(changePolicyBeneficiaryPersons(new Loading(action.payload.beneficiaryPersons)));

    const result: AsyncResult<string> = yield call(
      beneficiariesApi.updateBeneficiaryPercentages,
      action.payload.policyNumber,
      beneficiaryPersonPercentages
    );
    if (E.isRight(result)) {
      yield put(fetchBeneficiaryDetails(action.payload.policyNumber));
      yield put(createSnackbar('Beneficiary Percentages Successfully Saved'));
    } else {
      yield put(createSnackbar('Unable to Update Beneficiary Percentages'));
    }

    yield put(changePolicyBeneficiaryPersons(new Loaded(action.payload.beneficiaryPersons)));
  }
}

function* validateAndSaveBeneficiaryPolicySaga() {
  yield takeEvery(
    BeneficiaryDetailActionTypes.VALIDATE_AND_SAVE_BENEFICIARY_POLICY,
    validateAndSaveBeneficiaryPolicyApi
  );
}

function* validateAndSaveBeneficiaryPolicyApi(action: ReturnType<typeof validateAndSaveBeneficiaryPolicy>) {
  const beneficiaryPersons = action.payload.beneficiaryDetails.map(beneficiaryPerson =>
    ValidBeneficiaryPerson.create(beneficiaryPerson)
  );

  if (beneficiaryPersons.some(beneficiaryPerson => !(beneficiaryPerson instanceof ValidBeneficiaryPerson))) {
    yield put(updateBeneficiaryDetails(new Loaded(beneficiaryPersons as BeneficiaryPerson[])));
  } else {
    yield put(updateBeneficiaryDetails(new Loading(action.payload.beneficiaryDetails)));

    const result: AsyncResult<BeneficiaryPersonDto> = yield call(
      beneficiariesApi.updateBeneficiaryPolicy,
      action.payload.beneficiaryDetails.map(beneficiaryPerson => mapBeneficiaryPersonToDto(beneficiaryPerson)),
      action.payload.policyNumber
    );

    if (E.isRight(result)) {
      yield put(fetchBeneficiaryDetails(action.payload.policyNumber));
      yield put(createSnackbar('Beneficiary Policy Successfully Updated'));
    } else {
      yield put(createSnackbar('Unable to Update Beneficiary Policy'));
    }
  }
}

export default function*() {
  yield all([
    fetchBeneficiariesSaga(),
    fetchBeneficiaryDetailsSaga(),
    deleteBeneficiaryPersonSaga(),
    validateAndSubmitFormSaga(),
    validateAndSaveBeneficiaryPercentagesSaga(),
    validateAndSaveBeneficiaryPolicySaga(),
  ]);
}
